import React, { useRef, useEffect } from 'react';
import { Menu } from '@headlessui/react';
import { createPortal } from 'react-dom';
import { ArrowDownAZ, ArrowUpAZ, Clock, Hash, FolderTree, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDirectTheme } from '../../hooks/useDirectTheme';
import { SortMenuProps, SORT_OPTIONS } from './types';
import { getSortMenuStyles } from './styles';
import { cn } from '../../utils/cn';
import { useMenu } from '../../contexts/MenuContext';
import { Z_LAYERS } from '../../constants/zLayers';

export default React.memo(function SortMenu({
  view,
  currentPromptSort,
  currentFolderSort,
  onSortChange,
  variant = 'default',
  className,
}: SortMenuProps) {
  const { getDirectColor } = useDirectTheme();
  const [isOpen, setIsOpen] = React.useState(false);
  const [menuPosition, setMenuPosition] = React.useState({ top: 0, left: 0 });
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const styles = getSortMenuStyles(view, variant);
  const menuId = useRef(`sort-menu-${Math.random().toString(36).substr(2, 9)}`);
  const { registerMenu, unregisterMenu } = useMenu();
  const [zIndex, setZIndex] = React.useState<number>(Z_LAYERS.DROPDOWN);

  const updateMenuPosition = React.useCallback(() => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const menuWidth = view === 'sidepanel' ? 192 : 256; // w-48 = 192px, w-64 = 256px

      // Calculate left position
      let left = buttonRect.right - menuWidth;
      // Ensure menu doesn't go off screen
      if (left + menuWidth > viewportWidth) {
        left = viewportWidth - menuWidth - 16; // 16px padding from edge
      }
      if (left < 16) {
        left = 16; // 16px padding from edge
      }

      setMenuPosition({
        top: buttonRect.bottom + 8,
        left,
      });
    }
  }, [view]);

  useEffect(() => {
    if (isOpen) {
      updateMenuPosition();
      const newZIndex = registerMenu(menuId.current);
      setZIndex(newZIndex);
      window.addEventListener('scroll', updateMenuPosition);
      window.addEventListener('resize', updateMenuPosition);
      return () => {
        window.removeEventListener('scroll', updateMenuPosition);
        window.removeEventListener('resize', updateMenuPosition);
      };
    } else {
      unregisterMenu(menuId.current);
      setZIndex(Z_LAYERS.DROPDOWN);
    }
  }, [isOpen, updateMenuPosition, registerMenu, unregisterMenu]);

  const getIcon = (option: string) => {
    if (option.startsWith('alpha')) {
      return option === 'alpha-asc' ? (
        <ArrowDownAZ className={styles.icon} style={{ color: getDirectColor('actionIcon') }} />
      ) : (
        <ArrowUpAZ className={styles.icon} style={{ color: getDirectColor('actionIcon') }} />
      );
    }
    if (option.startsWith('date')) {
      return <Clock className={styles.icon} style={{ color: getDirectColor('actionIcon') }} />;
    }
    return <Hash className={styles.icon} style={{ color: getDirectColor('actionIcon') }} />;
  };

  return (
    <Menu as="div" className={cn(styles.container, className)} style={{ zIndex: Z_LAYERS.CONTENT }}>
      <Menu.Button
        onClick={() => {
          updateMenuPosition(); // Ensure menu position updates before opening
          setIsOpen(true);
        }}
        ref={buttonRef}
        as={motion.button}
        className={styles.button}
        style={{ color: getDirectColor('text') }}
        whileHover={{
          scale: 1.05,
          backgroundColor: getDirectColor('menuBackgroundHover'),
        }}
        whileTap={{ scale: 0.95 }}
        initial={{ backgroundColor: getDirectColor('backgroundTransparent') }}
      >
        <span>Sort</span>
        <svg
          className="w-6 h-6"
          style={{ color: getDirectColor('text') }}
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8 20V10m0 10-3-3m3 3 3-3m5-13v10m0-10 3 3m-3-3-3 3"
          />
        </svg>
      </Menu.Button>

      {isOpen &&
        createPortal(
          <Menu.Items
            className={styles.menu}
            style={{
              backgroundColor: getDirectColor('menuBackground'),
              borderColor: getDirectColor('border'),
              top: menuPosition.top,
              left: menuPosition.left,
              zIndex,
            }}
          >
            {/* Prompts Section */}
            <div className={styles.section} style={{ borderColor: getDirectColor('border') }}>
              <div
                className={styles.sectionHeader}
                style={{ color: getDirectColor('description') }}
              >
                <FileText className={styles.icon} style={{ color: getDirectColor('actionIcon') }} />
                <span>Sort Prompts</span>
              </div>
              <div className={styles.optionsList}>
                {SORT_OPTIONS.prompts.map(option => (
                  <Menu.Item key={option.value}>
                    {({ active }) => (
                      <motion.button
                        className={styles.option(currentPromptSort === option.value)}
                        style={{
                          color: getDirectColor(
                            currentPromptSort === option.value ? 'accent' : 'text'
                          ),
                          backgroundColor: active
                            ? getDirectColor('menuBackgroundHover')
                            : 'transparent',
                        }}
                        onClick={() => {
                          onSortChange('prompts', option.value);
                          setIsOpen(false);
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {getIcon(option.value)}
                        <span>{option.label}</span>
                      </motion.button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-2" />

            {/* Folders Section */}
            <div className={styles.section}>
              <div
                className={styles.sectionHeader}
                style={{ color: getDirectColor('description') }}
              >
                <FolderTree
                  className={styles.icon}
                  style={{ color: getDirectColor('actionIcon') }}
                />
                <span>Sort Folders</span>
              </div>
              <div className={styles.optionsList}>
                {SORT_OPTIONS.folders.map(option => (
                  <Menu.Item key={option.value}>
                    {({ active }) => (
                      <motion.button
                        className={styles.option(currentFolderSort === option.value)}
                        style={{
                          color: getDirectColor(
                            currentFolderSort === option.value ? 'accent' : 'text'
                          ),
                          backgroundColor: active
                            ? getDirectColor('menuBackgroundHover')
                            : 'transparent',
                        }}
                        onClick={() => {
                          onSortChange('folders', option.value);
                          setIsOpen(false);
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {getIcon(option.value)}
                        <span>{option.label}</span>
                      </motion.button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </div>
          </Menu.Items>,
          document.getElementById('portal-root') || document.body
        )}

      {isOpen && (
        <div
          className="fixed inset-0"
          style={{ zIndex: zIndex - 1 }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </Menu>
  );
});
